/**
 * Practice identity normalization (HA-350).
 *
 * Cognito stores the practice on the JWT in a COMPOSITE form:
 *   `custom:practice_name = "<Practice Name>#<practice-admin-email>"`
 *   e.g. "Playwright Automation#info+playwrightadmin@dosapes.com"
 *
 * The backend's canonical practice identifier is JUST the name portion —
 * everything before the first `#` (see the .NET
 * `PracticeIdentityHelper.Normalize` / `PracticeIdentifier.Canonical`).
 *
 * Any frontend code that derives a practiceId from `custom:practice_name`
 * and sends it to the API MUST normalize first. Sending the raw composite:
 *   1. 403s — the backend compares against the canonical name, so the
 *      composite never matches and the request is rejected (this is what
 *      made /templates show "No templates found" for practice admins).
 *   2. Leaks PII — the composite embeds the practice-admin EMAIL, which
 *      then lands in the API URL / query string (logged server-side).
 *   3. Breaks routing — the `+` in an email becomes `%2B` once
 *      URL-encoded, which IIS rejects with a 404.11 (path/query rejection).
 *
 * Normalizing to the canonical name (`"Playwright Automation"`) fixes all
 * three. The helper is idempotent: an already-canonical value is returned
 * unchanged, so it is safe to wrap any practiceId source.
 */

/**
 * Strip the `#<email>` suffix from a raw `custom:practice_name` claim to get
 * the canonical practice name the backend expects.
 *
 * Mirrors the backend `PracticeIdentityHelper.Normalize`:
 * everything from the first `#` onward is dropped and surrounding
 * whitespace is trimmed.
 *
 * @param raw The raw practice identifier (composite `Name#email` or already
 *   canonical). `undefined`/`null` are treated as empty.
 * @returns The canonical practice name, or `""` when no value is present.
 */
export const normalizePracticeId = (raw?: string | null): string =>
  (raw ?? '').split('#')[0].trim();

/**
 * Minimal shape of the decoded ID-token identity that carries the practice
 * claim. Kept local so this util has no dependency on the auth/identity
 * type modules (which would create import cycles for an API-layer helper).
 */
interface PracticeClaim {
  'custom:practice_name'?: string | null;
}

/**
 * Convenience accessor: read `custom:practice_name` off a decoded identity
 * and return the canonical practice name. Equivalent to
 * `normalizePracticeId(identity?.['custom:practice_name'])`.
 *
 * @param identity Decoded ID-token data (e.g. from `getIDTokenData()`).
 * @returns The canonical practice name, or `""` when unavailable.
 */
export const getPracticeId = (identity?: PracticeClaim | null): string =>
  normalizePracticeId(identity?.['custom:practice_name']);
